import { ButtonHTMLAttributes, FunctionComponent, ReactNode } from 'react';
import styled from 'styled-components';

import { DefaultButtonStyled } from './DefaultButton';

interface StickyButtonProps {
  children: ReactNode;
  hasGradient?: boolean;
  division?: boolean;
}

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  text: ReactNode;
}

type StickyButtonComponent = FunctionComponent<StickyButtonProps> & {
  Button: FunctionComponent<ButtonProps>;
  SubButton: FunctionComponent<ButtonProps>;
  WhiteSubButton: FunctionComponent<ButtonProps>;
};

const StickyButton: StickyButtonComponent = ({ children, hasGradient = true, ...props }: StickyButtonProps) => {
  return (
    <StickyButtonContainer id='stickyButton' {...props}>
      {hasGradient && <StickyButtonWrapperGradient />}
      <StickyButtonWrapper>{children}</StickyButtonWrapper>
    </StickyButtonContainer>
  );
};

const Button = ({ text, ...buttonProps }: ButtonProps) => (
  <ButtonWrapper>
    <StyledButton {...buttonProps}>{text}</StyledButton>
  </ButtonWrapper>
);

const SubButton = ({ text, ...buttonProps }: ButtonProps) => (
  <SubButtonWrapper>
    <StyledSubButton {...buttonProps}>{text}</StyledSubButton>
  </SubButtonWrapper>
);

const WhiteSubButton = ({ text, ...buttonProps }: ButtonProps) => (
  <SubButtonWrapper>
    <StyledWhiteSubButton {...buttonProps}>{text}</StyledWhiteSubButton>
  </SubButtonWrapper>
);

StickyButton.Button = Button;
StickyButton.SubButton = SubButton;
StickyButton.WhiteSubButton = WhiteSubButton;

export default StickyButton;

const StickyButtonContainer = styled.div<{ division?: boolean }>`
  position: sticky;
  bottom: 0;
  left: 0;
  margin: 0 auto;
  width: 100%;
  z-index: 50;
`;

const StickyButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  margin: 0 auto;
  padding: 0 20px 16px;

  div + div {
    margin-left: 10px;
  }

  ${({ theme }) => `
    max-width: ${theme.size.tablet};
    background-color: ${theme.color.white};
  `};
`;

const StickyButtonWrapperGradient = styled.div`
  height: 32px;
  width: 100%;

  right: 0;
  top: -32px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, #ffffff 100%);
`;

const ButtonWrapper = styled.div`
  flex: 7;
`;

const StyledDefaultButton = styled(DefaultButtonStyled)`
  width: 100%;
  border-radius: 12px;
  padding: 17px;
  height: 60px;

  ${({ theme }) => `
    ${theme.typography.UI18SB};
    background: ${theme.color.primary600};
    color: ${theme.color.white};
  
    &:disabled {
      background: ${theme.color.gray200};
    }
  `}
`;

const StyledButton = styled(StyledDefaultButton)`
  ${({ theme }) => `
    background: ${theme.color.primary600};
    &: active:not(:disabled) {
      background: ${theme.color.primary700};
    }
  `};
`;

const SubButtonWrapper = styled.div`
  flex: 3;
`;

const StyledSubButton = styled(StyledDefaultButton)`
  ${({ theme }) => `
    background: ${theme.color.gray700};
    &: active {
      background: ${theme.color.gray800};
    }
  `}
`;
const StyledWhiteSubButton = styled(StyledDefaultButton)`
  ${({ theme }) => `
    background: ${theme.color.white};
    color: ${theme.color.gray600};
    border: 1px solid ${theme.color.gray100};
    
    &: active {
      background: ${theme.color.white};
    }
  `}
`;
